import { useCallback, useMemo, memo } from 'react';
import _, { get, keys } from 'lodash';
import { List } from 'react-virtualized';
import { SlicerOptionRow } from '../../slicer-option/slicer-option.styles';
import { RightChevron } from '../../../../icons';
import { SCROLL_ROW_HEIGHT, useSlicerTheme } from '../../slicer-widget/common';
import {
  DatePickerWrapper,
  SlicerLabel,
  SlicerOptionSubMenuWrapper,
  StyledList,
  PopperStyled,
} from './timestamp-slicer-options.styles';
import { TimeSlicerOptionSubMenu } from './slicer-option-sub-menu';
import { useSlicerDimensions } from '../../slicer-widget/common/slicer-dimensions.hook';
import { ClickOutsideListener } from '../../../../components/ClickOutsideListener';
import { TimestampSlicerTypes } from '../../interfaces';
import {
  ListButtonStyled,
  PaperStyled,
} from '../../../../components/nested-dropdown/nested-dropdown.styles';
import {
  IOptionRowProps,
  ITimestampSlicerOptionsProps,
} from './timestamp-slicer-options.interfaces';
import { ZonedDateTime } from '@internationalized/date';
import {
  DatePicker,
  DateRangePicker,
} from '../../../../components/calendar/date-picker';
import { useTimestampSlicerOptionHooks } from './timestamp-slicer-options.hook';

const timestampOptionTypes = keys(TimestampSlicerTypes);

export const OptionRow = memo<IOptionRowProps>(
  ({
    id,
    vizId,
    name: fieldName,
    type: optionType,
    style,
    breadcrumbState,
    selectTimeFrame,
  }) => {
    const {
      subMenuRef,
      anchorRightPos,
      width,
      disablePortal,
      handleOnClick,
      setCurrAndPrev,
      setInPast,
      setSinceTimeFrame,
      setBetweenTimeFrame,
      currPrevDate,
      iPastDate,
      sinceDate,
      betweenDates,
      isOpen,
      optionLabel,
      resizeElement,
      isMobile,
      closeSubMenu,
    } = useTimestampSlicerOptionHooks({
      vizId,
      fieldName,
      optionType,
      breadcrumbState,
      selectTimeFrame,
    });

    const renderedSubMenu = useMemo(() => {
      switch (optionType) {
        case TimestampSlicerTypes.CURRENT_AND_PREV: {
          return (
            <TimeSlicerOptionSubMenu
              setOperands={setCurrAndPrev}
              fieldName={fieldName}
              type={TimestampSlicerTypes.CURRENT_AND_PREV}
              defaultValue={currPrevDate}
            />
          );
        }
        case TimestampSlicerTypes.IN_PAST: {
          return (
            <TimeSlicerOptionSubMenu
              setOperands={setInPast}
              fieldName={fieldName}
              type={TimestampSlicerTypes.IN_PAST}
              defaultValue={iPastDate}
            />
          );
        }
        case TimestampSlicerTypes.SINCE_PERIOD: {
          return (
            <DatePickerWrapper className='corvana-calendar-picker since-calendar'>
              <DatePicker
                isOpen
                onChange={setSinceTimeFrame}
                defaultValue={sinceDate}
              />
            </DatePickerWrapper>
          );
        }
        case TimestampSlicerTypes.BETWEEN_PERIOD: {
          return (
            <DatePickerWrapper className='corvana-calendar-picker between-calendar'>
              <DateRangePicker
                ref={subMenuRef}
                isOpen
                onChange={({
                  start,
                  end,
                }: {
                  start: ZonedDateTime;
                  end: ZonedDateTime;
                }) => setBetweenTimeFrame([start, end])}
                defaultValue={betweenDates}
              />
            </DatePickerWrapper>
          );
        }
        default: {
          return null;
        }
      }
    }, [
      optionType,
      setCurrAndPrev,
      fieldName,
      currPrevDate,
      setInPast,
      iPastDate,
      sinceDate,
      setSinceTimeFrame,
      subMenuRef,
      betweenDates,
      setBetweenTimeFrame,
    ]);

    const isCalendarSelection = useMemo(() => {
      return [
        TimestampSlicerTypes.SINCE_PERIOD,
        TimestampSlicerTypes.BETWEEN_PERIOD,
      ].includes(optionType);
    }, [optionType]);

    return (
      <SlicerOptionRow
        id={id}
        key={id}
        style={
          disablePortal
            ? { ...style, height: 'initial', minWidth: '100%' }
            : style
        }
        width={width}
        className='combo-menu-item'
      >
        <ListButtonStyled
          key={'popper-control'}
          aria-owns={open ? id : undefined}
          aria-haspopup={true}
          onClick={handleOnClick}
          disableRipple
        >
          <SlicerLabel
            height={style.height}
            isOpen={isOpen}
            className='main-timestamp-option'
          >
            <span>{optionLabel}</span>
            <RightChevron size={16} />
          </SlicerLabel>
          <PopperStyled
            key={'popper-content'}
            id={id}
            anchorEl={resizeElement}
            open={isOpen}
            placement={'right-start'}
            onClick={e => e.stopPropagation()}
            disablePortal={disablePortal}
            isCalendarSelection={isCalendarSelection}
            popperOptions={
              disablePortal
                ? {
                    modifiers: [
                      {
                        name: 'popperOffsets',
                        phase: 'read',
                        enabled: true,
                        fn: ({ state }) => ({
                          ...state,
                          popperOffsets: {
                            x: anchorRightPos ?? 0,
                            y: 0,
                          },
                        }),
                      },
                    ],
                  }
                : {}
            }
          >
            <PaperStyled square>
              <ClickOutsideListener
                isChildrenVisible={isOpen}
                onClickAway={e => {
                  if (
                    !(
                      e?.target?.tagName === 'BUTTON' ||
                      isMobile ||
                      resizeElement.contains(e.target) ||
                      subMenuRef?.current?.contains(e.target)
                    )
                  ) {
                    closeSubMenu();
                  }
                }}
              >
                <SlicerOptionSubMenuWrapper isSubMenuOpen={isOpen}>
                  {renderedSubMenu}
                </SlicerOptionSubMenuWrapper>
              </ClickOutsideListener>
            </PaperStyled>
          </PopperStyled>
        </ListButtonStyled>
      </SlicerOptionRow>
    );
  },
);

export const TimestampSlicerOptions = memo<ITimestampSlicerOptionsProps>(
  ({
    vizId,
    name: fieldName,
    slicerErrorVisible,
    breadcrumbState,
    selectTimeFrame,
  }) => {
    const { isDashletMode, isMobile } = useSlicerTheme();
    const { height, width } = useSlicerDimensions({
      optionsLength: timestampOptionTypes?.length,
      slicerErrorVisible,
    });

    const RowRenderer = useCallback(
      ({ key, index, style }) => {
        const optionType = get(
          timestampOptionTypes,
          index,
        ) as TimestampSlicerTypes;
        return (
          <OptionRow
            id={`slicer-option-row-${_.kebabCase(fieldName)}__${_.kebabCase(
              optionType,
            )}`}
            name={fieldName}
            key={key}
            style={{
              ...style,
              maxHeight:
                isDashletMode || isMobile ? SCROLL_ROW_HEIGHT : 'initial',
            }}
            type={optionType}
            selectTimeFrame={selectTimeFrame}
            vizId={vizId}
            breadcrumbState={breadcrumbState}
          />
        );
      },
      [
        fieldName,
        isDashletMode,
        isMobile,
        selectTimeFrame,
        vizId,
        breadcrumbState,
      ],
    );

    return (
      <StyledList>
        <List
          width={width}
          height={height}
          style={{ overflow: 'inherit', maxHeight: height }}
          rowCount={timestampOptionTypes?.length}
          rowHeight={SCROLL_ROW_HEIGHT}
          rowRenderer={RowRenderer}
        />
      </StyledList>
    );
  },
);
